import React from "react";
import { createPortal } from 'react-dom';
import { Input, Button, Divider, Upload, Progress, Tooltip, Radio, Alert } from "antd";
import { InfoCircleTwoTone } from "@ant-design/icons";
import EditableTable from "@/components/EditableTable";
import Panel from "@/components/Panel";
import { downloadLargeCSVInChunks, parseLargeCSVInChunks } from "../../../services/helper/csvFileHandle";
import { generateTransformDataSource, parseTransformDataSource, fileNameErrorTip, getButtonTip, getFileNameAndSuffix, AVAILABLE_STATUS } from "../../../services/download/sParameterHelper";
import { transformSParameter } from "../../../services/download/sParameterCtrl";
import { checkNameFormat } from "../../../services/helper/nameFormatCheck";
import './index.css';

const defaultColumns = [
  {
    title: 'Original Port Index',
    dataIndex: 'originPortIndex',
    key: 'originPortIndex',
    width: 80,
    render: (value) => (<span>{Number(value) + 1}</span>)
  },
  {
    title: 'Port Index',
    dataIndex: 'portIndex',
    key: 'portIndex',
    width: 80,
    render: (_, __, index) => (<span>{index + 1}</span>)
  },
  {
    title: 'Original Name',
    dataIndex: 'originName',
    key: 'originName',
  },
  {
    title: 'New Name',
    dataIndex: 'name',
    key: 'name',
  },
]

const formatOptions = [
  { label: 'MA', value: 'ma', title: 'Magnitude/Phase(deg)' },
  { label: 'RI', value: 'ri', title: 'Real/Imaginary' },
  { label: 'DB', value: 'db', title: 'dB/Phase(deg)' },
];

/**
 * transform S-parameter file
 * @param {{
 *  openTransformData: {
 *    snpUrl: string,
 *    fileName: string,
 *    portIndexNameMap: {[index]: [portName]},
 *    isOnlyRead: boolean,
 *  }
 * }} props 
 */
class Transform extends React.Component {

  constructor() {
    super()
    this.state = {
      selectedRowKeys: [],
      dataSource: [],
      fileName: "originName",
      downloadProgress: 0,
      uploadProgress: 0,
      transformColumns: [],
      transformLoading: false,
      fileNameError: "",
      uploadError: "",
      transformResInfo: {}, // {status: true, msg: "Transform success."}
      format: 'ma',
      transformSnpUrl: "",
    }

    this.dialogRoot = document.getElementById('root');
    this.fileNameList = [];
  }

  componentDidMount() {
    const { openTransformData } = this.props;
    const { fileName, fileSuffix, fileNameList } = !openTransformData.isOnlyRead ? getFileNameAndSuffix(openTransformData) : {};
    this.setState({
      fileName,
      fileSuffix,
      dataSource: generateTransformDataSource(openTransformData.portIndexNameMap),
      transformColumns: this.getTransformColumns(openTransformData.isOnlyRead),
    })
    this.fileNameList = fileNameList;
  }

  componentDidUpdate(prevProps) {
    const { openTransformData, isUpdateTransformSnp } = this.props;
    const { transformSnpUrl } = this.state;
    if ((prevProps.openTransformData || {}).snpUrl !== openTransformData.snpUrl) {
      const { fileName, fileSuffix, fileNameList } = !openTransformData.isOnlyRead ? getFileNameAndSuffix(openTransformData) : {};
      this.setState({
        fileName,
        fileSuffix,
        dataSource: generateTransformDataSource(openTransformData.portIndexNameMap),
        fileNameList,
        transformSnpUrl: "",
      })
      this.fileNameList = fileNameList;
    } else if (transformSnpUrl && isUpdateTransformSnp) { // need to get the transform result after the transform is complete
      const transformedSnpInfo = openTransformData.transformedSnpInfo || [];
      const transformFile = transformedSnpInfo.find(item => item.snpUrl === transformSnpUrl);
      // transformFile is deleted -> failed
      if (!transformFile) {
        this.setState({
          transformResInfo: { status: false, msg: "Transform failed." },
          transformSnpUrl: "",
          transformLoading: false,
        })
      } else if (transformFile.status === AVAILABLE_STATUS) { // transformFile status is available -> success
        this.setState({
          transformResInfo: { status: true, msg: "Transform success." },
          transformSnpUrl: "",
          transformLoading: false,
        })
      }
    }

    if (prevProps.openTransformData.isOnlyRead !== openTransformData.isOnlyRead) {
      this.setState({
        transformColumns: this.getTransformColumns(openTransformData.isOnlyRead),
      })
    }

    // Update fileNamesList
    if (isUpdateTransformSnp && prevProps.isUpdateTransformSnp !== isUpdateTransformSnp && !openTransformData.isOnlyRead) {
      const fileNameList = getFileNameAndSuffix(openTransformData, true);
      this.fileNameList = fileNameList;
      this.props.updateIsUpdateTransformSnp();
    }
  }

  getTransformColumns = (isOnlyRead) => {
    return isOnlyRead
      ? [{
        title: 'Port Index',
        dataIndex: 'originPortIndex',
        key: 'originPortIndex',
        render: (value) => (<span>{Number(value) + 1}</span>)
      }, {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
      }]
      : defaultColumns.map(column => {
        if (column.key === 'name') {
          return {
            ...column,
            onCell: (record) => ({
              record,
              edit: true,
              dataIndex: column.dataIndex,
              handleSave: this.changePortName,
            })
          }
        }

        return column;
      });
  }

  moveRow = (dragIndex, hoverIndex) => {
    const { dataSource, transformLoading } = this.state;
    if (transformLoading) return;
    const dragRow = dataSource[dragIndex];
    const newData = [...dataSource];
    newData.splice(dragIndex, 1);
    newData.splice(hoverIndex, 0, dragRow);
    this.setState({ dataSource: newData });
  };

  moveRows = (dragIndices, hoverIndex) => {
    const { dataSource, transformLoading } = this.state;
    if (transformLoading) return;
    const newData = [...dataSource];
    const hoverKey = newData[hoverIndex].originPortIndex;
    newData.splice(dragIndices[0], dragIndices.length);
    const _hoverIndex = newData.findIndex((item) => item.originPortIndex === hoverKey);
    newData.splice(_hoverIndex + (dragIndices[0] < hoverIndex ? 1 : 0), 0, ...dragIndices.map(index => dataSource[index]));
    this.setState({ dataSource: newData })
  };

  transform = async () => {
    const { openTransformData } = this.props;
    const { downloadProgress, uploadProgress, fileNameError, fileName } = this.state;

    if (!openTransformData.snpUrl || downloadProgress || uploadProgress || fileNameError) return;

    // After the last Transform is finished, clicking again will show a name duplication, which needs to be checked
    if (this.fileNameList.includes(fileName)) {
      this.setState({ fileNameError: `${fileName} already exists!` });
      return;
    }

    this.setState({
      transformLoading: true,
      transformResInfo: {}
    })
    const { fileSuffix, dataSource, format } = this.state;
    const portMappings = dataSource.map((item, index) => ({
      newIndex: index,
      newName: item.name,
      originalIndex: item.originPortIndex,
      originalName: item.originName,
    }))

    try {
      const res = await transformSParameter({
        portMappings,
        snpUrl: openTransformData.snpUrl,
        targetFile: `${fileName}${fileSuffix}`,
        format,
      })
      if (!res) {
        this.setState({
          transformResInfo: { status: false, msg: res && res.msg ? res.msg : "Transform failed." },
          transformLoading: false,
        })
      } else {
        // update tree list
        this.props.updateTimeInterval("create");
        this.setState({
          transformSnpUrl: res.snpUrl,
        })
      }
    } catch (error) {
      console.error(error);
      this.setState({
        transformResInfo: { status: false, msg: error || "Transform failed." },
        transformLoading: false,
      })
    }
  }

  downloadTransformTable = () => {
    const { dataSource, fileName, fileNameError } = this.state;
    if (fileNameError) return;

    downloadLargeCSVInChunks(
      dataSource.map((item, index) => ({
        ...item,
        portIndex: index + 1,
        originPortIndex: Number(item.originPortIndex) + 1,
      })),
      [
        { key: "originPortIndex", title: "Original Port Index" },
        { key: "portIndex", title: "Port Index" },
        { key: "originName", title: "Original Name" },
        { key: "name", title: "New Name" },
      ],
      `${fileName}.csv`,
      undefined,
      (progress) => this.uploadProcess(progress, 'downloadProgress')
    )
  }

  updateDataSource = (data) => {
    const dataSource = this.state.dataSource;
    const res = parseTransformDataSource(data, dataSource);
    if (typeof res === 'string') {
      this.setState({
        uploadError: res,
      })
      return;
    }

    this.setState({
      dataSource: res,
      uploadError: "",
    })
  }

  uploadTransformTable = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      parseLargeCSVInChunks(
        text,
        {
          "Original Port Index": "originPortIndex",
          "Port Index": "portIndex",
          "Original Name": "originName",
          "New Name": "name"
        },
        1000,
        (progress) => this.uploadProcess(progress, 'uploadProgress'),
        this.updateDataSource
      )
    };
    reader.readAsText(file);
    return false;
  }

  uploadProcess = (progress, type) => {
    this.setState({
      [type]: progress,
    })
  }

  changePortName = (newRecord) => {
    const { dataSource, transformLoading } = this.state;
    if (transformLoading) return;
    this.setState({
      dataSource: dataSource.map((item) => {
        if (item.originPortIndex === newRecord.originPortIndex) {
          return {
            ...item,
            name: newRecord.name,
          }
        }
        return item;
      }),
    })
  }

  saveFileName = () => {
    const name = this.state.fileName;
    //check name format
    const errorMsg = checkNameFormat(
      name,
      {
        reg: /^[a-zA-Z0-9][a-zA-Z0-9_\-$()]$/,
        nameErrorTip: fileNameErrorTip
      }
    );
    if (errorMsg) {
      this.setState({ fileNameError: errorMsg });
      return;
    }
    //check name repeated
    if (this.fileNameList.includes(name)) {
      this.setState({ fileNameError: `${name} already exists!` });
      return;
    }

    this.setState({ fileNameError: "", fileName: name });
  }

  changeFormat = (e) => {
    this.setState({ format: e.target.value });
  }

  getTipDiv = ({ status, msg }, elementType, defaultValue) => {
    if (typeof status !== 'boolean' || !msg) {
      return null;
    }

    return <Alert
      message={msg}
      type={status ? "success" : "error"}
      closable
      showIcon
      afterClose={() => this.setState({ [elementType]: defaultValue })}
      className="transform-panel-tip"
    />
  }

  handleToolRender = () => {
    const {
      fileName,
      fileSuffix,
      downloadProgress,
      uploadProgress,
      transformLoading,
      fileNameError,
      uploadError,
      transformResInfo,
      format
    } = this.state;
    return <>
      <div className="transform-input-box">
        <label>Format</label>
        <Radio.Group options={formatOptions} onChange={this.changeFormat} value={format} />
      </div>
      {/* Input */}
      <div className="transform-input-box">
        <label>Transformed File Name</label>
        <Tooltip
          title={fileNameError}
          open={!!fileNameError}
          classNames={{ root: "transform-panel-tooltip-error" }}
          destroyOnHidden
        >
          <Input
            onChange={(e) => this.setState({ fileName: e.target.value })}
            value={fileName}
            addonAfter={fileSuffix}
            disabled={transformLoading}
            onPressEnter={this.saveFileName}
            onBlur={this.saveFileName}
          />
        </Tooltip>
        <Button
          onClick={this.transform}
          color="primary"
          variant="outlined"
          loading={transformLoading}
        >Transform</Button>
      </div>
      {/* Transform tip */}
      {this.getTipDiv(transformResInfo, "transformResInfo", {})}
      <Divider className="transform-line-divider" />
      <div className={"transform-botton-box" + (downloadProgress || uploadProgress ? ' transform-botton-box-loading' : '')}>
        <Tooltip title={getButtonTip(downloadProgress, uploadProgress)}>
          <Button
            color="primary"
            variant="outlined"
            loading={!!uploadProgress}
            onClick={() => this.downloadTransformTable()}
            disabled={transformLoading}
          >
            Download Transform Config
          </Button>
        </Tooltip>
        <Upload {...{
          name: 'file',
          accept: '.csv',
          beforeUpload: this.uploadTransformTable,
          showUploadList: false,
          disabled: !!uploadProgress || transformLoading,
        }}>
          <Tooltip title={getButtonTip(downloadProgress, uploadProgress)}>
            <Button
              color="primary"
              variant="outlined"
              loading={!!uploadProgress}
              disabled={transformLoading}
            >
              Upload Transform Config
            </Button>
          </Tooltip>
        </Upload>
      </div>
      {downloadProgress || uploadProgress
        ? <Progress percent={downloadProgress || uploadProgress} className="transform-progress" />
        : null}
      {/* Upload tip */}
      {this.getTipDiv({ status: false, msg: uploadError }, "uploadError", "")}
      {/* Drag sort tip */}
      <div className="transform-drag-tip">
        <InfoCircleTwoTone /> Drag single rows to reorder. When multiple rows are checked, only consecutive ones move together.
      </div>
    </>
  }

  render() {
    const { selectedRowKeys, dataSource, transformColumns } = this.state;
    const { openTransformData } = this.props;
    const { isOnlyRead, fileName } = openTransformData;

    const content = (
      <Panel
        title={isOnlyRead ? fileName : `Transform — ${fileName}`}
        onCancel={this.props.closeModal}
        zIndex={2001}
        position="panel-center-top"
        draggable
        minHeight={200}
        minWidth={400}
        maxHeight={800}
        maxWidth={800}
        overflow="auto"
        className="transform-panel"
        top={-100}
        left={200}
      >
        <div className="transform-content">
          {!isOnlyRead ? this.handleToolRender() : null}
          {/* Table */}
          <EditableTable
            rowKey="originPortIndex"
            className="transform-table"
            columns={transformColumns}
            dataSource={dataSource}
            selectedRowKeys={selectedRowKeys}
            draggable={!isOnlyRead}
            rowSelection={isOnlyRead ? undefined : {
              type: 'checkbox',
              onChange: (selectedRowKeys) => this.setState({ selectedRowKeys }),
            }}
            moveRows={this.moveRows}
            moveRow={this.moveRow}
            selectedIndices={selectedRowKeys.map(key => dataSource.findIndex(item => item.originPortIndex === key))}
            scroll={dataSource.length > 12 ? { y: 40 * 12 } : undefined}
          />
        </div>
      </Panel>
    )
    return createPortal(content, this.dialogRoot);
  }
}

export default Transform;